// VRG-674 — GTM (internal). API client for `/internal/gtm/*`.
//
// Types declared locally, like seoApi.ts. Canonical definitions:
// `apps/api/src/features/gtm/gtm-articles.service.ts`. Keep in step.

import type {
  AddContentIdeaSourceRequest,
  ContentCorpusOption,
  ContentIdea,
  ContentFeedRun,
  ContentRecommendationBatch,
  DecideRecommendationRequest,
  SuggestRecommendationsRequest,
  SuggestRecommendationsResult,
  UnmappedCorpusItem,
  ContentIdeaKeywordOption,
  CreateContentIdeaRequest,
  SetContentIdeaStatusRequest,
  UpdateContentIdeaRequest,
  AutoWonPlan,
  ClientTier,
  CommercePlatform,
  CommercePlatformSource,
  CreateSocialDraftRequest,
  DealPlan,
  DealSource,
  CreateSocialStatRequest,
  DealClosedReason,
  DealForecast,
  FoundersListStatus,
  FoundersListTerms,
  LeadSourceGroup,
  MasterFacet,
  MasterFacetOption,
  MasterPipeline,
  MasterPipelineFilters,
  OutreachCampaignType,
  OutreachTargetStatus,
  PipelineChannelCounts,
  PipelineDeal,
  PipelineFlow,
  PipelineKpis,
  PayingNowStatus,
  SalesBoardLane,
  SalesSetBy,
  SalesStage,
  SocialClaimCandidate,
  SocialDraft,
  SocialPublishHealth,
  SocialPublishVerifyResult,
  SocialStat,
  SourceChannel,
  StageEvidence,
  StageProbabilities,
  TagEconomics,
  SubSourceKind,
  SubSourceOption,
  SubSourceRef,
  UpdateSocialDraftRequest,
  UpdateSocialStatRequest,
} from '@repo/types'

import { MASTER_FACETS } from '@repo/types'

import { createApiCall } from './index'

export interface ArticleApproval {
  section: 'insights' | 'learn'
  slug: string
  contentHash: string
  title: string
  approvedBy: string | null
  note: string | null
  approvedAt: string
}

/**
 * VRG-685 — an article's PUBLICATION, owned by vera-2 (the website reads the published set).
 * Canonical: `apps/api/src/features/gtm/website-articles.mapper.ts#ArticlePublicationView`.
 * Only articles that have EVER been published have a row; no row = never published.
 */
export type RevalidateStatus = 'pending' | 'sent' | 'failed' | 'not_configured'
export interface ArticlePublication {
  section: 'insights' | 'learn'
  slug: string
  title: string
  isPublished: boolean
  firstPublishedAt: string | null
  publishedAt: string | null
  publishedBy: string | null
  unpublishedAt: string | null
  unpublishedBy: string | null
  contentHash: string | null
  version: number
  revalidate: { status: RevalidateStatus; attempts: number; error: string | null; at: string | null }
}


// VRG-691 — leads, prospect companies and deals. Canonical: `apps/api/src/features/gtm/sales/
// sales-pipeline.service.ts` (the card) and `@repo/types` gtm/sales-pipeline (the folds).
export interface SalesPerson {
  id: string
  name: string
  email: string
}
export interface SalesLeadSource {
  channel: SourceChannel | null
  detail: string | null
  ref: string | null
  ref_kind: string | null
  set_by: SalesSetBy | null
  group: LeadSourceGroup
  /** VRG-701 — the typed sub-source behind `detail` (a pick, or the campaign Vera found). */
  sub_source: SubSourceRef | null
}
export interface SalesDealCard extends PipelineDeal {
  account_name: string
  /** VRG-701 — the deal's own stored source (null = inherits the company's), and who set it. */
  deal_source: { value: DealSource | null; set_by: SalesSetBy | null }
  /** VRG-701 — the company's first-touch source. */
  company_source: SalesLeadSource
  /** VRG-701 — the deal's people (its own, else the company's). */
  people: Array<{ id: string; name: string | null; email: string }>
  people_explicit: boolean
  /** VRG-701 — the plan a paying subscription sets (Stripe), and the one set by hand. */
  stripe_plan: DealPlan | null
  plan_manual: DealPlan | null
  account_domain: string | null
  lane: SalesBoardLane
  stage_set_by: SalesSetBy
  stage_changed_at: string
  days_in_stage: number
  closed_at: string | null
  closed_note: string | null
  evidence: StageEvidence
  source: SalesLeadSource
  owner: SalesPerson | null
  expected_close_on: string | null
  next_step: string | null
  lead_count: number
  primary_lead: { name: string | null; email: string } | null
  company: { id: string; name: string } | null
  demo_company: { id: string; name: string } | null
  roi_headline: { total: number; currency: string | null } | null
  value_manual: { amount: number; currency: string } | null
  /**
   * VG-27 — the Founder's List terms applied to THIS deal (whether the brand is on the list is its
   * company's `founders_list_status`). All four null on an ordinary deal.
   */
  founders_terms: FoundersListTerms
  /** VRG-692 — predicted vs actual ROI (brand) and ARR (Vera), with accuracy. */
  forecast: DealForecast
}
export interface SalesBoard {
  range: { since: string | null; until: string | null }
  deals: SalesDealCard[]
  kpis: PipelineKpis
  probabilities: StageProbabilities
  /** VRG-736 — the tag unit cost and markup behind every deal's tag-margin line (placeholders until VRG-143). */
  tag_economics: TagEconomics
  staff: SalesPerson[]
  leads_without_company: number
  computed_at: string
}
export interface SalesLeadRow {
  id: string
  email: string
  name: string | null
  title: string | null
  phone: string | null
  notes: string | null
  account: { id: string; name: string } | null
  signup: { id: string; status: string; created_at: string; granted_at: string | null } | null
  person_channel: SourceChannel | null
  origin: string
  created_at: string
  /** VRG-692 — the lead's company's current deal forecast, or null with no company. */
  forecast: DealForecast | null
  /** VRG-700 — the lead's company's deal marks (Won with nothing paying; moved to Won by Vera). */
  deal_flags: SalesDealFlags | null
}
/** VRG-700 — mirrors `DealFlags` in apps/api sales-pipeline.service.ts. */
export interface SalesDealFlags {
  won_without_paying: boolean
  auto_won: { subscription_id: string; at: string } | null
  /** VRG-727 — the deal's board lane and state: the People tab's stage chip reads them. */
  lane: SalesBoardLane
  status: 'open' | 'closed'
  closed_reason: DealClosedReason | null
  /** VRG-727 — the company's money status (Paying now / Not paying / Never paid). */
  paying_now: PayingNowStatus
}
export interface SalesAccountRow {
  id: string
  name: string
  domain: string | null
  industry: string | null
  size: string | null
  /** VRG-701 — the client tier, and the one the calculator's production suggests. */
  tier: ClientTier | null
  suggested_tier: { tier: ClientTier; units: number } | null
  country: string | null
  /** VRG-696 (0152). */
  city: string | null
  notes: string | null
  /** VRG-716 (0160) — the storefront's platform, and whether it was detected or set by a person. */
  commerce_platform: CommercePlatform | null
  commerce_platform_source: CommercePlatformSource | null
  /** VG-27 (0171) — the brand's place on the Founder's List, or null for not on it. Internal only. */
  founders_list_status: FoundersListStatus | null
  owner: SalesPerson | null
  source: SalesLeadSource
  company: { id: string; name: string } | null
  demo_company: { id: string; name: string } | null
  origin: string
  lead_count: number
  /** VRG-701 — the primary deal (open and not Won, else Won, else latest closed). */
  deal: SalesDealCard | null
  /** VRG-701 — every deal at the company: open first. */
  deals: SalesDealCard[]
  created_at: string
  /**
   * VRG-718 — the linked platform company's Activated milestone (first billable activation, ISO)
   * and billable activation counts. Null when no platform company is linked. Read-only; a
   * milestone, not a deal stage. Present on the list only.
   */
  activation?: { first_activated_at: string | null; activations_count: number; activations_recent: number } | null
}
export interface SalesDealEvent {
  id: string
  /** VRG-701 — the deal it is on. */
  deal_id: string
  kind: 'opened' | 'stage' | 'evidence' | 'closed' | 'reopened' | 'override' | string
  from_stage: SalesStage | null
  to_stage: SalesStage | null
  set_by: SalesSetBy
  evidence: string | null
  note: string | null
  occurred_at: string
  recorded_at: string
  actor: string | null
  /** VRG-700 — the Stripe subscription an automatic Won, its reopen, or an override is about. */
  subscription_id: string | null
}
export interface SalesAccountDetail extends SalesAccountRow {
  leads: SalesLeadRow[]
  events: SalesDealEvent[]
  closed_deals: SalesDealCard[]
}
export interface SalesAccountInput {
  name?: string
  domain?: string | null
  industry?: string | null
  size?: string | null
  tier?: ClientTier | null
  country?: string | null
  city?: string | null
  notes?: string | null
  /** VRG-716 — send only when a person changed it: it is stored as manual. null clears it. */
  commerce_platform?: CommercePlatform | null
  /** VG-27 — the Founder's List status; null = not on the list. */
  founders_list_status?: FoundersListStatus | null
  owner_user_id?: string | null
  source_channel?: SourceChannel | null
  source_detail?: string | null
  /** VRG-701 — a picked sub-source {kind, id}; null clears it. */
  source_sub?: { kind: SubSourceKind; id: string } | null
  source_reset?: boolean
}
export interface SalesLeadInput {
  email?: string
  name?: string | null
  title?: string | null
  phone?: string | null
  notes?: string | null
  account_id?: string | null
  new_account?: SalesAccountInput | null
}
/**
 * VRG-696 — `GET /internal/gtm/sales/flow`: THE flow query. GTM → Channels' master pipeline and
 * the Sales tab's flow chart + by-source table come from one filtered slice of one snapshot.
 * Mirrors `SalesFlow` in apps/api sales-pipeline.service.ts.
 */
export interface SalesFlow {
  range: { since: string | null; until: string | null }
  filters: MasterPipelineFilters
  master: MasterPipeline
  facets: Record<MasterFacet, MasterFacetOption[]>
  sales_flow: PipelineFlow
  by_channel: PipelineChannelCounts[]
  leads_without_company: number
  computed_at: string
}

/**
 * The flow query's filters as URL parameters: lists repeat, money is ANNUAL USD — always, whatever
 * the MRR/ARR switch shows (VRG-700: the inputs convert, the URL does not), so a link is stable.
 */
export const salesFlowQuery = (f: MasterPipelineFilters): string => {
  const p = new URLSearchParams()
  if (f.range?.since) p.set('since', f.range.since)
  if (f.range?.until) p.set('until', f.range.until)
  for (const k of MASTER_FACETS) for (const v of f[k] ?? []) p.append(k, v)
  for (const k of ['value_min', 'value_max', 'actual_min', 'actual_max'] as const) {
    const v = f[k]
    if (v !== null && v !== undefined && Number.isFinite(v)) p.set(k, String(v))
  }
  // VRG-701 — the company-lifetime view; the default (each deal under its own source) is unwritten.
  if (f.attribution === 'first_touch') p.set('attribution', 'first_touch')
  return p.toString()
}

export interface SalesDealInput {
  value_manual?: { amount: number; currency: string } | null
  expected_close_on?: string | null
  owner_user_id?: string | null
  next_step?: string | null
  /** VRG-701. */
  name?: string | null
  plan?: DealPlan | null
  /** A channel, 'expansion', or null = inherit the company's first touch. */
  source?: DealSource | null
  /** The deal's people; [] = the company's. */
  lead_ids?: string[]
  /** VRG-701 — the sub-source under the deal's own channel. */
  source_sub?: { kind: SubSourceKind; id: string } | null
  /** VRG-701 — the deal's own calculator inputs ("edited"); null = back to the company's calculator. */
  calc_inputs?: { units: number | null; price: number | null; cac: number | null } | null
  /** VRG-736 — share of production activated, a RATIO 0–1; null = the tier default. */
  activation_share?: number | null
  /**
   * VG-27 — the Founder's List terms on this deal; null clears each. `founders_bank_released` is a
   * BOOLEAN here and a timestamp in the database: the API stamps when, on the transition only.
   */
  founders_waived_base_months?: number | null
  founders_activation_bank?: number | null
  founders_bank_released?: boolean | null
  founders_term_ends_on?: string | null
}

/** VRG-701 — `GET /internal/gtm/sales/sub-sources?channel=`. Mirrors `SubSourceList` in the API. */
export interface SalesSubSourceList {
  channel: SourceChannel
  kind: Exclude<SubSourceKind, 'saved'> | null
  options: SubSourceOption[]
  saved: SubSourceOption[]
}

/** VRG-701 — `POST /internal/gtm/sales/deals`. Mirrors `CreateDealInput` in the API service. */
export interface SalesCreateDealInput extends SalesDealInput {
  account_id?: string | null
  new_account?: SalesAccountInput | null
  new_leads?: Array<{ email: string; name?: string | null; title?: string | null }>
  stage?: SalesStage
}
export interface SalesCreateDealResult {
  id: string
  account_id: string
  warnings: Array<'open_deal_exists'>
}

/** `createApiCall` resolves on error statuses (services/index.ts), so the status is checked here. */
async function call<T>(method: 'GET' | 'PUT' | 'POST' | 'PATCH' | 'DELETE', url: string, what: string, data?: unknown): Promise<T> {
  const response = await createApiCall({ method, url, data: data ?? {} })
  if (response.status < 200 || response.status >= 300) {
    const error = new Error(response?.data?.message || `Failed to ${what}`) as Error & {
      status?: number
      code?: string
      missing?: string[]
      data?: unknown
    }
    error.status = response?.status ?? 0
    // The body travels with the error: Social's 409 on Approve carries the lint findings.
    error.data = response?.data
    // VRG-691 — a refused or to-be-confirmed stage move carries a code and the missing stages.
    error.code = typeof response?.data?.code === 'string' ? response.data.code : undefined
    error.missing = Array.isArray(response?.data?.missing) ? response.data.missing : undefined
    throw error
  }
  return response.data as T
}

const articlePath = (section: string, slug: string) =>
  `/internal/gtm/articles/${encodeURIComponent(section)}/${encodeURIComponent(slug)}/approval`
const publicationPath = (section: string, slug: string, action: 'publish' | 'unpublish' | 'refresh') =>
  `/internal/gtm/articles/${encodeURIComponent(section)}/${encodeURIComponent(slug)}/${action}`

// VRG-675 — Sales pipeline + source channels. Canonical: `gtm-sales.service.ts`,
// `gtm-source-channels.service.ts`.
export type PipelineStage = 'signups' | 'demos' | 'companies' | 'clients'
export interface PipelineChannelRow {
  channel: SourceChannel | null
  counts: Record<PipelineStage, number>
}
export interface SalesPipeline {
  range: { since: string; until: string }
  stages: PipelineStage[]
  totals: Record<PipelineStage, number>
  byChannel: PipelineChannelRow[]
  sources: { accounts: boolean; apollo: boolean }
  computedAt: string
}
export interface SourceChannelMap {
  people: Record<string, SourceChannel>
  companies: Record<string, SourceChannel>
}

// VRG-679 — Outreach. Canonical: `gtm-outreach.service.ts`.
export interface OutreachCampaign {
  id: string
  name: string
  type: OutreachCampaignType
  city: string | null
  starts_on: string | null
  ends_on: string | null
  notes: string | null
  /** VRG-684 — what the trip cost, in the currency paid; null when not recorded. */
  trip_cost: { amount: number; currency: string } | null
  created_at: string
  updated_at: string
  counts: Record<OutreachTargetStatus, number>
  total: number
}
export interface OutreachTarget {
  id: string
  campaign_id: string
  brand_name: string
  contact_name: string | null
  contact_email: string | null
  industry: string | null
  website: string | null
  status: OutreachTargetStatus
  status_changed_at: string
  meeting_at: string | null
  meeting_notes: string | null
  notes: string | null
  person_key: string | null
  demo_company: { id: string; name: string } | null
  created_at: string
}
export type OutreachCampaignInput = Partial<
  Pick<OutreachCampaign, 'name' | 'type' | 'city' | 'starts_on' | 'ends_on' | 'notes' | 'trip_cost'>
>
export type OutreachTargetInput = Partial<
  Pick<OutreachTarget, 'brand_name' | 'contact_name' | 'contact_email' | 'industry' | 'website' | 'status' | 'meeting_at' | 'meeting_notes' | 'notes'>
>
const outreach = '/internal/gtm/outreach'
const social = '/internal/gtm/social'
const ideas = '/internal/gtm/content-ideas'
const sales = '/internal/gtm/sales'

export const gtmApi = {
  outreachCampaigns: (): Promise<OutreachCampaign[]> => call('GET', `${outreach}/campaigns`, 'load outreach campaigns'),
  outreachCampaign: (id: string): Promise<{ campaign: OutreachCampaign; targets: OutreachTarget[] }> =>
    call('GET', `${outreach}/campaigns/${encodeURIComponent(id)}`, 'load the campaign'),
  createOutreachCampaign: (input: OutreachCampaignInput) =>
    call<{ id: string }>('POST', `${outreach}/campaigns`, 'create the campaign', input),
  updateOutreachCampaign: (id: string, input: OutreachCampaignInput) =>
    call<{ ok: true }>('PATCH', `${outreach}/campaigns/${encodeURIComponent(id)}`, 'save the campaign', input),
  deleteOutreachCampaign: (id: string) =>
    call<{ ok: true }>('DELETE', `${outreach}/campaigns/${encodeURIComponent(id)}`, 'delete the campaign'),
  addOutreachTarget: (campaignId: string, input: OutreachTargetInput) =>
    call<{ id: string }>('POST', `${outreach}/campaigns/${encodeURIComponent(campaignId)}/targets`, 'add the brand', input),
  updateOutreachTarget: (id: string, input: OutreachTargetInput) =>
    call<{ ok: true }>('PATCH', `${outreach}/targets/${encodeURIComponent(id)}`, 'save the brand', input),
  deleteOutreachTarget: (id: string) =>
    call<{ ok: true }>('DELETE', `${outreach}/targets/${encodeURIComponent(id)}`, 'remove the brand'),
  linkOutreachPerson: (id: string) =>
    call<{ person_key: string }>('POST', `${outreach}/targets/${encodeURIComponent(id)}/link-person`, 'link the contact to Access'),
  unlinkOutreachPerson: (id: string) =>
    call<{ ok: true }>('DELETE', `${outreach}/targets/${encodeURIComponent(id)}/link-person`, 'unlink from Access'),
  linkOutreachDemo: (id: string, companyId: string) =>
    call<{ ok: true }>(
      'POST',
      `${outreach}/targets/${encodeURIComponent(id)}/link-demo/${encodeURIComponent(companyId)}`,
      'link the demo',
    ),
  unlinkOutreachDemo: (id: string) =>
    call<{ ok: true }>('DELETE', `${outreach}/targets/${encodeURIComponent(id)}/link-demo`, 'unlink the demo'),
  // VRG-691 — the deals pipeline, leads and prospect companies.
  salesBoard: (range: { since?: string; until?: string } | null): Promise<SalesBoard> =>
    call(
      'GET',
      range?.since || range?.until
        ? `${sales}/board?since=${encodeURIComponent(range.since ?? '')}&until=${encodeURIComponent(range.until ?? '')}`
        : `${sales}/board`,
      'load the deals',
    ),
  salesFlow: (filters: MasterPipelineFilters): Promise<SalesFlow> => {
    const q = salesFlowQuery(filters)
    return call('GET', q ? `${sales}/flow?${q}` : `${sales}/flow`, 'load the pipeline flow')
  },
  /** VRG-700 — the dry run: which deals a paying subscription would move to Won, and why others would not. */
  salesAutoWonPreview: (): Promise<AutoWonPlan> => call('GET', `${sales}/auto-won/preview`, 'load the automatic Won preview'),
  salesAccounts: (): Promise<SalesAccountRow[]> => call('GET', `${sales}/accounts`, 'load the companies'),
  salesAccount: (id: string): Promise<SalesAccountDetail> => call('GET', `${sales}/accounts/${encodeURIComponent(id)}`, 'load the company'),
  salesLeads: (): Promise<SalesLeadRow[]> => call('GET', `${sales}/leads`, 'load the leads'),
  createSalesAccount: (input: SalesAccountInput) =>
    call<{ id: string; deal_id: string }>('POST', `${sales}/accounts`, 'add the company', input),
  updateSalesAccount: (id: string, input: SalesAccountInput) =>
    call<{ ok: true }>('PATCH', `${sales}/accounts/${encodeURIComponent(id)}`, 'save the company', input),
  createSalesLead: (input: SalesLeadInput) =>
    call<{ id: string; account_id: string | null }>('POST', `${sales}/leads`, 'add the lead', input),
  updateSalesLead: (id: string, input: SalesLeadInput) =>
    call<{ ok: true }>('PATCH', `${sales}/leads/${encodeURIComponent(id)}`, 'save the lead', input),
  deleteSalesLead: (id: string) => call<{ ok: true }>('DELETE', `${sales}/leads/${encodeURIComponent(id)}`, 'remove the lead'),
  salesSubSources: (channel: SourceChannel): Promise<SalesSubSourceList> =>
    call('GET', `${sales}/sub-sources?channel=${encodeURIComponent(channel)}`, 'load the sub-sources'),
  addSalesSubSource: (channel: SourceChannel, label: string) =>
    call<SubSourceOption>('POST', `${sales}/sub-sources`, 'add the sub-source', { channel, label }),
  createSalesDeal: (input: SalesCreateDealInput) => call<SalesCreateDealResult>('POST', `${sales}/deals`, 'add the deal', input),
  updateSalesDeal: (id: string, input: SalesDealInput) =>
    call<{ ok: true }>('PATCH', `${sales}/deals/${encodeURIComponent(id)}`, 'save the deal', input),
  /** `reopen`: the card was dragged out of the Lost column (VRG-692) — reopen it, then move. */
  moveSalesDeal: (id: string, toStage: SalesStage, confirm = false, reopen = false) =>
    call<{ ok: true; stage: SalesStage; set_by: SalesSetBy; reopened?: boolean }>('POST', `${sales}/deals/${encodeURIComponent(id)}/move`, 'move the deal', {
      to_stage: toStage,
      confirm,
      reopen,
    }),
  closeSalesDeal: (id: string, reason: 'lost' | 'archived', note?: string | null) =>
    call<{ ok: true }>('POST', `${sales}/deals/${encodeURIComponent(id)}/close`, 'close the deal', { reason, note }),
  reopenSalesDeal: (id: string) => call<{ ok: true }>('POST', `${sales}/deals/${encodeURIComponent(id)}/reopen`, 'reopen the deal'),
  /** VRG-736 — markup is a RATIO (0.35 = 35%); null puts a field back to its placeholder. */
  updateSalesTagEconomics: (input: { unit_cost_usd?: number | null; markup?: number | null }) =>
    call<TagEconomics>('PUT', `${sales}/settings/tag-economics`, 'save the tag economics', input),
  updateSalesSettings: (stageProbabilities: StageProbabilities) =>
    call<StageProbabilities>('PUT', `${sales}/settings`, 'save the stage probabilities', { stage_probabilities: stageProbabilities }),
  salesPipeline: (range: { since: string; until: string }): Promise<SalesPipeline> =>
    call(
      'GET',
      `/internal/gtm/sales/pipeline?since=${encodeURIComponent(range.since)}&until=${encodeURIComponent(range.until)}`,
      'load the sales pipeline',
    ),
  sourceChannels: (): Promise<SourceChannelMap> =>
    call('GET', '/internal/gtm/source-channels', 'load source channels'),
  setPersonSourceChannel: (key: string, sourceChannel: SourceChannel | null) =>
    call<{ ok: true }>('PUT', '/internal/gtm/source-channels/person', "set the person's source channel", {
      key,
      source_channel: sourceChannel,
    }),
  setCompanySourceChannel: (companyId: string, sourceChannel: SourceChannel | null) =>
    call<{ ok: true }>(
      'PUT',
      `/internal/gtm/source-channels/company/${encodeURIComponent(companyId)}`,
      "set the company's source channel",
      { source_channel: sourceChannel },
    ),
  articleApprovals: (): Promise<ArticleApproval[]> =>
    call('GET', '/internal/gtm/articles/approvals', 'load article approvals'),
  approveArticle: (input: { section: string; slug: string; contentHash: string; title: string; note?: string }) =>
    call<ArticleApproval>('PUT', articlePath(input.section, input.slug), 'approve the article', {
      contentHash: input.contentHash,
      title: input.title,
      note: input.note,
    }),
  revokeArticleApproval: (section: string, slug: string) =>
    call<{ revoked: true }>('DELETE', articlePath(section, slug), 'withdraw the approval'),
  // VRG-685 — publication.
  articlePublications: (): Promise<ArticlePublication[]> =>
    call('GET', '/internal/gtm/articles/publications', 'load article publications'),
  publishArticle: (input: { section: string; slug: string; title: string; contentHash: string }) =>
    call<ArticlePublication>('POST', publicationPath(input.section, input.slug, 'publish'), 'publish the article', {
      title: input.title,
      contentHash: input.contentHash,
    }),
  unpublishArticle: (section: string, slug: string) =>
    call<ArticlePublication>('POST', publicationPath(section, slug, 'unpublish'), 'unpublish the article'),
  refreshArticleOnWebsite: (section: string, slug: string) =>
    call<ArticlePublication>('POST', publicationPath(section, slug, 'refresh'), 'refresh the website'),
  // VRG-740 — the content-ideas queue (GTM → Insights → Ideas, VRG-751). Canonical:
  // `apps/api/src/features/gtm/content-ideas/content-ideas.controller.ts` and `@repo/types`
  // gtm/content-ideas. snake_case JSON. The guest calls that sat here went with the second guest
  // list (VRG-752); the one list is `verafiedApi`.
  contentIdeas: (): Promise<ContentIdea[]> => call('GET', ideas, 'load the ideas'),
  contentIdea: (id: string): Promise<ContentIdea> => call('GET', `${ideas}/${encodeURIComponent(id)}`, 'load the idea'),
  createContentIdea: (input: CreateContentIdeaRequest) => call<ContentIdea>('POST', ideas, 'add the idea', input),
  updateContentIdea: (id: string, input: UpdateContentIdeaRequest) =>
    call<ContentIdea>('PATCH', `${ideas}/${encodeURIComponent(id)}`, 'save the idea', input),
  setContentIdeaStatus: (id: string, input: SetContentIdeaStatusRequest) =>
    call<ContentIdea>('POST', `${ideas}/${encodeURIComponent(id)}/status`, 'move the idea', input),
  addContentIdeaSource: (id: string, input: AddContentIdeaSourceRequest) =>
    call<ContentIdea>('POST', `${ideas}/${encodeURIComponent(id)}/sources`, 'add the research link', input),
  removeContentIdeaSource: (id: string, itemId: string) =>
    call<ContentIdea>('DELETE', `${ideas}/${encodeURIComponent(id)}/sources/${encodeURIComponent(itemId)}`, 'remove the research link'),
  feedContentIdeas: () => call<ContentFeedRun>('POST', `${ideas}/feed`, 'run the idea feeds'),
  // VRG-741 — feed runs, unmapped corpus items, recommendation cards.
  contentFeedRuns: (): Promise<ContentFeedRun[]> => call('GET', `${ideas}/feed-runs`, 'load the feed runs'),
  contentUnmapped: (): Promise<UnmappedCorpusItem[]> => call('GET', `${ideas}/unmapped`, 'load the unmapped items'),
  suggestRecommendations: (input: SuggestRecommendationsRequest) =>
    call<SuggestRecommendationsResult>('POST', `${ideas}/recommendations`, 'suggest recommendations', input),
  recommendationBatches: (): Promise<ContentRecommendationBatch[]> =>
    call('GET', `${ideas}/recommendations/batches`, 'load the recommendation batches'),
  decideRecommendation: (id: string, input: DecideRecommendationRequest) =>
    call<ContentIdea>('POST', `${ideas}/${encodeURIComponent(id)}/recommendation`, 'decide the recommendation', input),
  contentIdeaKeywords: (q: string): Promise<ContentIdeaKeywordOption[]> =>
    call('GET', `${ideas}/keywords?q=${encodeURIComponent(q)}`, 'search the keywords'),
  contentIdeaCorpus: (q: string): Promise<ContentCorpusOption[]> =>
    call('GET', `${ideas}/corpus?q=${encodeURIComponent(q)}`, 'search the research corpus'),
  // GTM → Social. Canonical: `apps/api/src/features/gtm/SOCIAL_CONTRACT.md`. snake_case JSON.
  socialStats: (): Promise<SocialStat[]> => call('GET', `${social}/stats`, 'load the stat registry'),
  socialClaimCandidates: (): Promise<SocialClaimCandidate[]> =>
    call('GET', `${social}/stats/claim-candidates`, 'load approved claims'),
  createSocialStat: (input: CreateSocialStatRequest) => call<SocialStat>('POST', `${social}/stats`, 'add the stat', input),
  updateSocialStat: (id: string, input: UpdateSocialStatRequest) =>
    call<SocialStat>('PATCH', `${social}/stats/${encodeURIComponent(id)}`, 'save the stat', input),
  socialDrafts: (): Promise<SocialDraft[]> => call('GET', `${social}/drafts`, 'load the drafts'),
  createSocialDraft: (input: CreateSocialDraftRequest) => call<SocialDraft>('POST', `${social}/drafts`, 'create the draft', input),
  updateSocialDraft: (id: string, input: UpdateSocialDraftRequest) =>
    call<SocialDraft>('PATCH', `${social}/drafts/${encodeURIComponent(id)}`, 'save the draft', input),
  approveSocialDraft: (id: string) => call<SocialDraft>('POST', `${social}/drafts/${encodeURIComponent(id)}/approve`, 'approve the draft'),
  rejectSocialDraft: (id: string) => call<SocialDraft>('POST', `${social}/drafts/${encodeURIComponent(id)}/reject`, 'reject the draft'),
  scheduleSocialDraft: (id: string, scheduled_for: string) =>
    call<SocialDraft>('POST', `${social}/drafts/${encodeURIComponent(id)}/schedule`, 'schedule the draft', { scheduled_for }),
  markSocialDraftPosted: (id: string) =>
    call<SocialDraft>('POST', `${social}/drafts/${encodeURIComponent(id)}/mark-posted`, 'mark the draft posted'),
  // Instagram publishing: connector health, a read-only credentials check, retry of a failed publication.
  socialPublishStatus: (): Promise<SocialPublishHealth> => call('GET', `${social}/publishing/status`, 'load the Instagram connection'),
  verifySocialPublishing: () =>
    call<SocialPublishVerifyResult>('POST', `${social}/publishing/verify`, 'check the Instagram connection'),
  retrySocialPublish: (id: string) =>
    call<SocialDraft>('POST', `${social}/drafts/${encodeURIComponent(id)}/publish/retry`, 'retry the publication'),
}

/** VRG-734 — the same request helper, for the GTM sub-APIs that live in their own file (`verafiedApi.ts`). */
export { call as gtmCall }
